import { useEffect, useMemo, useState } from "react";
import { Building2, Copy, Grid3x3, Layers, Plus, Search, Table2, Trash2, Users } from "lucide-react";
import { Badge, Button, Checkbox, Field, Input, NativeSelect, Popover, PopoverContent, PopoverTrigger, cn } from "@ncp/ui";
import { defaultsFor, labelOf, localize } from "../schema";
import type { I18nText, PartyValue, PropertySchema, RegistryHit } from "../types";
import { registerWidget, type WidgetProps } from "./registry";

const T = {
  add: { en: "Add", ar: "إضافة" },
  remove: { en: "Remove", ar: "إزالة" },
  duplicate: { en: "Duplicate", ar: "تكرار" },
  item: { en: "Item", ar: "عنصر" },
  empty: { en: "Nothing added yet.", ar: "لم تتم إضافة أي عنصر بعد." },
  addRow: { en: "Add row", ar: "إضافة صف" },
  total: { en: "Total", ar: "الإجمالي" },
  party: {
    role: { en: "Role", ar: "الدور" },
    name: { en: "Legal name", ar: "الاسم القانوني" },
    nameHint: { en: "Start typing to search the commercial registry.", ar: "ابدأ بالكتابة للبحث في السجل التجاري." },
    nameAr: { en: "Name (Arabic)", ar: "الاسم (بالعربية)" },
    registryNumber: { en: "Licence / registry no.", ar: "رقم الرخصة / السجل" },
    jurisdiction: { en: "Jurisdiction", ar: "الاختصاص القضائي" },
    emirate: { en: "Emirate", ar: "الإمارة" },
    contactName: { en: "Contact person", ar: "جهة الاتصال" },
    contactEmail: { en: "Contact e-mail", ar: "البريد الإلكتروني" },
    isSubmitter: { en: "Submitting party", ar: "الطرف مقدم الطلب" },
    verified: { en: "Matched in registry", ar: "مطابق في السجل" },
    addParty: { en: "Add party", ar: "إضافة طرف" },
    noParties: { en: "No parties added yet.", ar: "لم تتم إضافة أطراف بعد." },
  },
};

const DEFAULT_ROLES: Array<{ value: string; label: I18nText }> = [
  { value: "AcquiringParty", label: { en: "Acquiring party", ar: "الطرف المستحوذ" } },
  { value: "TargetParty", label: { en: "Target party", ar: "الطرف المستهدف" } },
  { value: "NotifyingParty", label: { en: "Notifying party", ar: "الطرف المبلغ" } },
  { value: "JointVenturePartner", label: { en: "Joint-venture partner", ar: "شريك في مشروع مشترك" } },
  { value: "Complainant", label: { en: "Complainant", ar: "مقدم الشكوى" } },
  { value: "Respondent", label: { en: "Respondent", ar: "المشكو في حقه" } },
  { value: "Representative", label: { en: "Representative", ar: "ممثل" } },
  { value: "Other", label: { en: "Other", ar: "أخرى" } },
];

function tr(t: I18nText, locale: "en" | "ar"): string {
  return localize(t, locale);
}

// ---------------------------------------------------------------- object group

export function ObjectWidget(props: WidgetProps) {
  const { schema, path, renderChild, options } = props;
  const columns = Number(options.columns ?? 2);
  const entries = Object.entries(schema.properties ?? {});
  if (!renderChild) return null;
  return (
    <div className={cn("grid gap-4", columns === 1 && "grid-cols-1", columns === 2 && "sm:grid-cols-2", columns === 3 && "sm:grid-cols-3", "rounded-xl border border-sand-200 bg-sand-50/40 p-4")}>
      {entries.map(([k, child]) => (
        <div key={k} className={cn(spanClass(child["x-ncp"]?.span, columns))}>{renderChild(`${path}.${k}`, child)}</div>
      ))}
    </div>
  );
}

function spanClass(span: number | undefined, columns: number): string {
  if (!span || span >= 12) return columns > 1 ? "sm:col-span-full" : "";
  if (span > 6) return columns >= 3 ? "sm:col-span-2" : "sm:col-span-full";
  return "";
}

// ---------------------------------------------------------------- repeatable array

export function ArrayWidget(props: WidgetProps) {
  const { schema, path, value, onChange, renderChild, disabled, readOnly, locale, options } = props;
  const items = Array.isArray(value) ? (value as unknown[]) : [];
  const itemSchema = schema.items ?? { type: "object", properties: {} };
  const max = schema.maxItems;
  const itemLabel = (options.itemLabel as I18nText | undefined) ?? T.item;
  const canEdit = !disabled && !readOnly;
  const add = () => onChange([...items, defaultsFor(itemSchema) ?? {}]);
  const remove = (i: number) => onChange(items.filter((_, j) => j !== i));
  const duplicate = (i: number) => onChange([...items.slice(0, i + 1), JSON.parse(JSON.stringify(items[i])), ...items.slice(i + 1)]);
  if (!renderChild) return null;
  return (
    <div className="space-y-3">
      {items.length === 0 ? <p className="rounded-xl border border-dashed border-sand-300 p-5 text-center text-sm text-muted-foreground">{tr(T.empty, locale)}</p> : null}
      {items.map((_, i) => (
        <div key={i} className="relative rounded-xl border border-sand-200 bg-white/70 p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-sand-600">{tr(itemLabel, locale)} {i + 1}</span>
            {canEdit ? (
              <div className="flex gap-1">
                <Button type="button" size="icon-sm" variant="ghost" onClick={() => duplicate(i)} aria-label={tr(T.duplicate, locale)} disabled={!!max && items.length >= max}><Copy /></Button>
                <Button type="button" size="icon-sm" variant="ghost" onClick={() => remove(i)} aria-label={tr(T.remove, locale)}><Trash2 className="text-danger-600" /></Button>
              </div>
            ) : null}
          </div>
          {itemSchema.properties ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {Object.entries(itemSchema.properties).map(([k, child]) => (
                <div key={k} className={spanClass(child["x-ncp"]?.span, 2)}>{renderChild(`${path}.${i}.${k}`, child)}</div>
              ))}
            </div>
          ) : (
            renderChild(`${path}.${i}`, itemSchema)
          )}
        </div>
      ))}
      {canEdit ? <Button type="button" variant="outline" size="sm" onClick={add} disabled={!!max && items.length >= max}><Plus /> {tr(T.add, locale)} {tr(itemLabel, locale).toLowerCase()}</Button> : null}
    </div>
  );
}

// ---------------------------------------------------------------- party

export function partySchema(): PropertySchema {
  return {
    type: "object",
    "x-ncp": { widget: "party" },
    properties: {
      role: { type: "string" },
      name: { type: "string", minLength: 2, maxLength: 200 },
      nameAr: { type: "string", maxLength: 200 },
      registryNumber: { type: "string", maxLength: 40 },
      jurisdiction: { type: "string", maxLength: 2, default: "AE" },
      emirate: { type: "string", maxLength: 40 },
      contactName: { type: "string", maxLength: 120 },
      contactEmail: { type: "string", format: "email" },
      isSubmitter: { type: "boolean" },
    },
    required: ["name"],
  };
}

function RegistryLookup({ value, onChange, onPick, disabled, locale, context, invalid, id }: { value: string; onChange: (v: string) => void; onPick: (h: RegistryHit) => void; disabled: boolean; locale: "en" | "ar"; context: WidgetProps["context"]; invalid: boolean; id: string }) {
  const [results, setResults] = useState<RegistryHit[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!context.searchRegistry || value.trim().length < 3) {
      setResults([]);
      return;
    }
    const search = context.searchRegistry;
    const h = setTimeout(() => {
      search(value)
        .then((r) => {
          setResults(r);
          setOpen(r.length > 0);
        })
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(h);
  }, [value, context]);
  const input = (
    <div className="relative">
      <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-sand-500" />
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} className="ps-9" disabled={disabled} aria-invalid={invalid || undefined} autoComplete="off" />
    </div>
  );
  if (!context.searchRegistry) return input;
  return (
    <Popover open={open && results.length > 0} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{input}</PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-1" onOpenAutoFocus={(e) => e.preventDefault()}>
        <ul className="max-h-64 overflow-auto">
          {results.map((r, i) => (
            <li key={i}>
              <button type="button" className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-start text-sm hover:bg-sand-100" onClick={() => { onPick(r); setOpen(false); }}>
                <Building2 className="mt-0.5 size-4 text-aegold-700" />
                <span className="min-w-0">
                  <span className="block truncate font-medium">{locale === "ar" && r.nameAr ? r.nameAr : r.nameEn}</span>
                  <span className="block truncate text-xs text-muted-foreground">{[r.licenseNumber, r.emirate, r.activity].filter(Boolean).join(" · ")}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export function PartyWidget(props: WidgetProps) {
  const { id, value, onChange, disabled, readOnly, invalid, locale, context, options, error } = props;
  const party = (value as PartyValue | undefined) ?? { name: "" };
  const canEdit = !disabled && !readOnly;
  const roles = (options.roles as Array<{ value: string; label: I18nText }> | undefined) ?? DEFAULT_ROLES;
  const hideFields = new Set((options.hide as string[] | undefined) ?? []);
  const showRole = !hideFields.has("role");
  const showContact = !hideFields.has("contact");
  const showSubmitter = !hideFields.has("isSubmitter");
  const set = (patch: Partial<PartyValue>) => onChange({ ...party, ...patch });
  return (
    <div className={cn("rounded-xl border bg-white/70 p-4", invalid ? "border-danger-300" : "border-sand-200")}>
      <div className="grid gap-3 md:grid-cols-[1fr_1.5fr_1fr]">
        {showRole ? (
          <Field label={tr(T.party.role, locale)}>
            {(fid) => (
              <NativeSelect id={fid} value={party.role ?? ""} onChange={(e) => set({ role: e.target.value })} disabled={!canEdit}>
                <option value="">—</option>
                {roles.map((r) => <option key={r.value} value={r.value}>{tr(r.label, locale)}</option>)}
              </NativeSelect>
            )}
          </Field>
        ) : null}
        <Field label={tr(T.party.name, locale)} required hint={context.searchRegistry ? tr(T.party.nameHint, locale) : undefined} error={error} className={showRole ? "" : "md:col-span-2"}>
          {(fid) => (
            <RegistryLookup id={fid || id} value={party.name ?? ""} onChange={(v) => set({ name: v })} onPick={(h) => set({ name: h.nameEn, nameAr: h.nameAr, registryNumber: h.licenseNumber, emirate: h.emirate, jurisdiction: "AE" })} disabled={!canEdit} locale={locale} context={context} invalid={invalid} />
          )}
        </Field>
        <Field label={tr(T.party.registryNumber, locale)}>{(fid) => <Input id={fid} value={party.registryNumber ?? ""} onChange={(e) => set({ registryNumber: e.target.value })} dir="ltr" disabled={!canEdit} />}</Field>
        <Field label={tr(T.party.nameAr, locale)}>{(fid) => <Input id={fid} value={party.nameAr ?? ""} onChange={(e) => set({ nameAr: e.target.value })} dir="rtl" className="font-arabic" disabled={!canEdit} />}</Field>
        <Field label={tr(T.party.jurisdiction, locale)}>{(fid) => <Input id={fid} value={party.jurisdiction ?? ""} onChange={(e) => set({ jurisdiction: e.target.value.toUpperCase().slice(0, 2) })} placeholder="AE" dir="ltr" disabled={!canEdit} />}</Field>
        <Field label={tr(T.party.emirate, locale)}>{(fid) => <Input id={fid} value={party.emirate ?? ""} onChange={(e) => set({ emirate: e.target.value })} disabled={!canEdit} />}</Field>
        {showContact ? (
          <>
            <Field label={tr(T.party.contactName, locale)}>{(fid) => <Input id={fid} value={party.contactName ?? ""} onChange={(e) => set({ contactName: e.target.value })} disabled={!canEdit} />}</Field>
            <Field label={tr(T.party.contactEmail, locale)}>{(fid) => <Input id={fid} type="email" value={party.contactEmail ?? ""} onChange={(e) => set({ contactEmail: e.target.value })} dir="ltr" disabled={!canEdit} />}</Field>
          </>
        ) : null}
        {showSubmitter ? (
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <Checkbox checked={party.isSubmitter === true} onCheckedChange={(v) => set({ isSubmitter: v === true })} disabled={!canEdit} /> {tr(T.party.isSubmitter, locale)}
          </label>
        ) : null}
      </div>
      {party.registryNumber ? <Badge variant="success" size="sm" className="mt-3"><Building2 /> {tr(T.party.verified, locale)}</Badge> : null}
    </div>
  );
}

export function PartyListWidget(props: WidgetProps) {
  const { value, onChange, disabled, readOnly, locale, schema } = props;
  const parties = Array.isArray(value) ? (value as PartyValue[]) : [];
  const canEdit = !disabled && !readOnly;
  const max = schema.maxItems;
  const update = (i: number, p: PartyValue) => onChange(parties.map((x, j) => (j === i ? p : x)));
  return (
    <div className="space-y-3">
      {parties.length === 0 ? <p className="rounded-xl border border-dashed border-sand-300 p-5 text-center text-sm text-muted-foreground">{tr(T.party.noParties, locale)}</p> : null}
      {parties.map((p, i) => (
        <div key={i} className="relative">
          <PartyWidget {...props} id={`${props.id}-${i}`} path={`${props.path}.${i}`} schema={schema.items ?? partySchema()} value={p} onChange={(v) => update(i, v as PartyValue)} error={undefined} />
          {canEdit ? <Button type="button" size="icon-sm" variant="ghost" className="absolute end-2 top-2" onClick={() => onChange(parties.filter((_, j) => j !== i))} aria-label={tr(T.remove, locale)}><Trash2 className="text-danger-600" /></Button> : null}
        </div>
      ))}
      {canEdit ? <Button type="button" variant="outline" size="sm" onClick={() => onChange([...parties, { role: parties.length === 0 ? "AcquiringParty" : "TargetParty", name: "", jurisdiction: "AE", isSubmitter: parties.length === 0 }])} disabled={!!max && parties.length >= max}><Plus /> {tr(T.party.addParty, locale)}</Button> : null}
    </div>
  );
}

// ---------------------------------------------------------------- generic table

export interface TableColumn {
  key: string;
  label: I18nText;
  type?: "text" | "number" | "currency" | "percent" | "select";
  choices?: Array<{ value: string; label: I18nText }>;
  width?: string;
  /** Column is filled from another array field (e.g. party names) and not editable. */
  readOnly?: boolean;
  total?: boolean;
}

function columnsFromSchema(schema: PropertySchema, locale: "en" | "ar"): TableColumn[] {
  const props = schema.items?.properties ?? {};
  return Object.entries(props).map(([key, p]) => ({
    key,
    label: labelOf(p, key, locale),
    type: p.format === "currency" ? "currency" : p.format === "percent" ? "percent" : p.type === "number" || p.type === "integer" ? "number" : p.enum ? "select" : "text",
    choices: p.enum?.map((v) => ({ value: String(v), label: (p["x-ncp"]?.enumOptions ?? []).find((o) => String(o.value) === String(v))?.label ?? String(v) })),
    total: p["x-ncp"]?.options?.total === true,
    readOnly: p["x-ncp"]?.readOnly,
  }));
}

export function TableWidget(props: WidgetProps) {
  const { value, onChange, disabled, readOnly, locale, options, schema, invalid, context } = props;
  const rows = Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];
  const columns = useMemo(() => (options.columns as TableColumn[] | undefined) ?? columnsFromSchema(schema, locale), [options.columns, schema, locale]);
  const canEdit = !disabled && !readOnly;
  const fixedRows = options.fixedRows === true;
  const currency = (options.currency as string | undefined) ?? "AED";
  const fmt = useMemo(() => new Intl.NumberFormat(locale === "ar" ? "ar-AE" : "en-AE", { maximumFractionDigits: 2 }), [locale]);
  const setCell = (i: number, key: string, v: unknown) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
  const addRow = () => onChange([...rows, Object.fromEntries(columns.map((c) => [c.key, c.type === "text" || c.type === "select" ? "" : undefined]))]);
  const totals = columns.filter((c) => c.total);
  return (
    <div className={cn("overflow-x-auto rounded-xl border bg-white/70", invalid ? "border-danger-300" : "border-sand-200")}>
      <table className="w-full text-sm">
        <thead className="bg-sand-50 text-xs uppercase tracking-wider text-sand-700">
          <tr>
            {columns.map((c) => <th key={c.key} className="px-3 py-2 text-start font-semibold" style={{ width: c.width }}>{tr(c.label, locale)}{c.type === "currency" ? ` (${currency})` : c.type === "percent" ? " (%)" : ""}</th>)}
            {canEdit && !fixedRows ? <th className="w-10" /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={columns.length + 1} className="px-3 py-5 text-center text-muted-foreground">{tr(T.empty, locale)}</td></tr> : null}
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-sand-100">
              {columns.map((c) => (
                <td key={c.key} className="px-2 py-1.5">
                  {c.readOnly || !canEdit ? (
                    <span className={cn("block px-1 py-1.5", c.type !== "text" && c.type !== "select" && "text-end tabular-nums")}>{formatCell(r[c.key], c, locale, currency, fmt, context.formatCurrency)}</span>
                  ) : c.type === "select" ? (
                    <NativeSelect value={String(r[c.key] ?? "")} onChange={(e) => setCell(i, c.key, e.target.value)} className="h-9">
                      <option value="">—</option>
                      {(c.choices ?? []).map((o) => <option key={o.value} value={o.value}>{tr(o.label, locale)}</option>)}
                    </NativeSelect>
                  ) : c.type === "text" ? (
                    <Input value={String(r[c.key] ?? "")} onChange={(e) => setCell(i, c.key, e.target.value)} className="h-9" />
                  ) : (
                    <Input inputMode="decimal" dir="ltr" className="h-9 text-end tabular-nums" value={r[c.key] === undefined || r[c.key] === null ? "" : String(r[c.key])} onChange={(e) => { const n = Number(e.target.value.replace(/[,\s]/g, "")); setCell(i, c.key, e.target.value === "" ? undefined : Number.isFinite(n) ? n : r[c.key]); }} />
                  )}
                </td>
              ))}
              {canEdit && !fixedRows ? <td className="px-1"><Button type="button" size="icon-sm" variant="ghost" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label={tr(T.remove, locale)}><Trash2 className="text-danger-600" /></Button></td> : null}
            </tr>
          ))}
        </tbody>
        {totals.length > 0 && rows.length > 0 ? (
          <tfoot className="border-t border-sand-200 bg-sand-50/60 font-semibold">
            <tr>
              {columns.map((c, idx) => (
                <td key={c.key} className={cn("px-3 py-2", c.total && "text-end tabular-nums")}>
                  {idx === 0 && !c.total ? tr(T.total, locale) : c.total ? formatCell(rows.reduce((acc, r) => acc + (Number(r[c.key]) || 0), 0), c, locale, currency, fmt, context.formatCurrency) : ""}
                </td>
              ))}
              {canEdit && !fixedRows ? <td /> : null}
            </tr>
          </tfoot>
        ) : null}
      </table>
      {canEdit && !fixedRows ? <div className="border-t border-sand-100 p-2"><Button type="button" variant="ghost" size="sm" onClick={addRow} disabled={!!schema.maxItems && rows.length >= schema.maxItems}><Plus /> {tr(T.addRow, locale)}</Button></div> : null}
    </div>
  );
}

function formatCell(v: unknown, c: TableColumn, locale: "en" | "ar", currency: string, fmt: Intl.NumberFormat, formatCurrency?: (v: number, c: string) => string): string {
  if (v === undefined || v === null || v === "") return "—";
  if (c.type === "currency") return typeof v === "number" ? (formatCurrency ? formatCurrency(v, currency) : fmt.format(v)) : String(v);
  if (c.type === "percent") return typeof v === "number" ? `${fmt.format(v)}%` : String(v);
  if (c.type === "number") return typeof v === "number" ? fmt.format(v) : String(v);
  if (c.type === "select") return tr(c.choices?.find((o) => o.value === String(v))?.label ?? String(v), locale);
  return String(v);
}

export function turnoverTableSchema(): PropertySchema {
  return {
    type: "array",
    minItems: 1,
    "x-ncp": { widget: "turnoverTable", options: { currency: "AED" } },
    items: {
      type: "object",
      properties: {
        party: { type: "string", "x-ncp": { title: { en: "Party", ar: "الطرف" } } },
        year: { type: "integer", minimum: 2000, maximum: 2100, "x-ncp": { title: { en: "Financial year", ar: "السنة المالية" } } },
        uaeTurnover: { type: "number", format: "currency", minimum: 0, "x-ncp": { title: { en: "UAE turnover", ar: "الإيرادات في الإمارات" }, options: { total: true } } },
        worldwideTurnover: { type: "number", format: "currency", minimum: 0, "x-ncp": { title: { en: "Worldwide turnover", ar: "الإيرادات العالمية" }, options: { total: true } } },
      },
      required: ["party", "uaeTurnover"],
    },
  };
}

export function marketTableSchema(): PropertySchema {
  return {
    type: "array",
    minItems: 1,
    "x-ncp": { widget: "marketTable" },
    items: {
      type: "object",
      properties: {
        market: { type: "string", minLength: 2, "x-ncp": { title: { en: "Relevant market", ar: "السوق المعنية" } } },
        geography: { type: "string", enum: ["UAE", "Emirate", "GCC", "Global"], "x-ncp": { title: { en: "Geographic scope", ar: "النطاق الجغرافي" }, enumOptions: [{ value: "UAE", label: { en: "UAE", ar: "الإمارات" } }, { value: "Emirate", label: { en: "Single emirate", ar: "إمارة واحدة" } }, { value: "GCC", label: { en: "GCC", ar: "دول الخليج" } }, { value: "Global", label: { en: "Global", ar: "عالمي" } }] } },
        shareBefore: { type: "number", format: "percent", minimum: 0, maximum: 100, "x-ncp": { title: { en: "Share before", ar: "الحصة قبل" } } },
        shareAfter: { type: "number", format: "percent", minimum: 0, maximum: 100, "x-ncp": { title: { en: "Combined share", ar: "الحصة المجمعة" } } },
      },
      required: ["market", "geography"],
    },
  };
}

// ---------------------------------------------------------------- registration

export function registerCompositeWidgets(): void {
  registerWidget({ key: "object", label: { en: "Field group", ar: "مجموعة حقول" }, icon: Layers, category: "composite", forTypes: ["object"], component: ObjectWidget, defaultSchema: () => ({ type: "object", properties: { first: { type: "string" }, second: { type: "string" } } }), defaultSpan: 12, optionFields: [{ key: "columns", label: { en: "Columns", ar: "الأعمدة" }, type: "number" }] });
  registerWidget({ key: "array", label: { en: "Repeating group", ar: "مجموعة متكررة" }, icon: Grid3x3, category: "composite", forTypes: ["array"], component: ArrayWidget, defaultSchema: () => ({ type: "array", items: { type: "object", properties: { name: { type: "string" }, value: { type: "string" } } } }), defaultSpan: 12, optionFields: [{ key: "itemLabel", label: { en: "Item label", ar: "تسمية العنصر" }, type: "text" }] });
  registerWidget({ key: "party", label: { en: "Party (registry lookup)", ar: "طرف (بحث في السجل)" }, description: { en: "A legal entity with commercial-registry lookup, bilingual name and contact.", ar: "كيان قانوني مع بحث في السجل التجاري واسم ثنائي اللغة وجهة اتصال." }, icon: Building2, category: "composite", forTypes: ["object"], component: PartyWidget, defaultSchema: partySchema, defaultSpan: 12, optionFields: [{ key: "hide", label: { en: "Hide fields", ar: "إخفاء حقول" }, type: "list", hint: { en: "role, contact, isSubmitter", ar: "role, contact, isSubmitter" } }] });
  registerWidget({ key: "partyList", label: { en: "Parties", ar: "الأطراف" }, description: { en: "Repeatable party cards (acquirer, target, complainant…).", ar: "بطاقات أطراف متكررة (المستحوذ، المستهدف، مقدم الشكوى…)." }, icon: Users, category: "composite", forTypes: ["array"], component: PartyListWidget, defaultSchema: () => ({ type: "array", minItems: 1, items: partySchema(), "x-ncp": { widget: "partyList" } }), defaultSpan: 12 });
  registerWidget({ key: "table", label: { en: "Table", ar: "جدول" }, icon: Table2, category: "composite", forTypes: ["array"], component: TableWidget, defaultSchema: () => ({ type: "array", items: { type: "object", properties: { item: { type: "string" }, amount: { type: "number", format: "currency" } } }, "x-ncp": { widget: "table" } }), defaultSpan: 12, optionFields: [{ key: "fixedRows", label: { en: "Fixed rows", ar: "صفوف ثابتة" }, type: "boolean" }, { key: "currency", label: { en: "Currency", ar: "العملة" }, type: "select", choices: ["AED", "USD", "EUR"] }] });
  registerWidget({ key: "turnoverTable", label: { en: "Turnover table", ar: "جدول الإيرادات" }, description: { en: "UAE and worldwide turnover per party with totals.", ar: "الإيرادات في الإمارات وعالميا لكل طرف مع الإجماليات." }, icon: Table2, category: "composite", forTypes: ["array"], component: TableWidget, defaultSchema: turnoverTableSchema, defaultSpan: 12, optionFields: [{ key: "currency", label: { en: "Currency", ar: "العملة" }, type: "select", choices: ["AED", "USD", "EUR"] }] });
  registerWidget({ key: "marketTable", label: { en: "Market shares table", ar: "جدول الحصص السوقية" }, description: { en: "Relevant markets with geographic scope and shares.", ar: "الأسواق المعنية مع النطاق الجغرافي والحصص." }, icon: Table2, category: "composite", forTypes: ["array"], component: TableWidget, defaultSchema: marketTableSchema, defaultSpan: 12 });
}
